import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Search, Save } from 'lucide-react';

interface ArticleBrief {
  id: string;
  title: string;
  summaryMd: string | null;
  lessonId: string | null;
  tags: string[];
}

interface ArticleFull extends ArticleBrief {
  bodyMd: string;
}

export default function AdminContentPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ title: string; bodyMd: string } | null>(null);

  const { data: list, isLoading } = useQuery<ArticleBrief[]>({
    queryKey: ['admin-articles', q],
    queryFn: () =>
      api.get<ArticleBrief[]>(`/api/admin/articles${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });

  const { data: open } = useQuery<ArticleFull>({
    queryKey: ['admin-article', openId],
    queryFn: () => api.get<ArticleFull>(`/api/training/articles/${openId}`),
    enabled: !!openId,
  });

  const save = useMutation({
    mutationFn: (args: { id: string; title: string; bodyMd: string }) =>
      api.put<{ ok: true }>(`/api/admin/articles/${args.id}`, {
        title: args.title,
        bodyMd: args.bodyMd,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-articles'] });
      qc.invalidateQueries({ queryKey: ['admin-article', openId] });
      setDraft(null);
      setOpenId(null);
    },
  });

  if (openId && open) {
    const d = draft ?? { title: open.title, bodyMd: open.bodyMd };
    return (
      <div className="safe-bottom min-h-full">
        <TopBar title={`Править #${open.id}`} subtitle={open.lessonId ? `Урок ${open.lessonId}` : 'Без урока'} />
        <div className="p-4 space-y-3">
          <Card className="space-y-2">
            <label className="text-sm text-tg-hint">Заголовок</label>
            <input
              value={d.title}
              onChange={(e) => setDraft({ ...d, title: e.target.value })}
              className="w-full h-11 px-3 bg-tg-secondaryBg rounded-xl outline-none"
            />
            <label className="text-sm text-tg-hint mt-2 block">Текст (markdown)</label>
            <textarea
              value={d.bodyMd}
              onChange={(e) => setDraft({ ...d, bodyMd: e.target.value })}
              className="w-full h-96 p-2 bg-tg-secondaryBg rounded-xl outline-none font-mono text-xs"
            />
            <div className="flex gap-2 mt-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setDraft(null);
                  setOpenId(null);
                }}
                disabled={save.isPending}
              >
                Отмена
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({ id: open.id, title: d.title, bodyMd: d.bodyMd })
                }
              >
                <Save className="w-4 h-4 mr-2" />
                Сохранить
              </Button>
            </div>
          </Card>
          <Card className="text-xs text-tg-hint">
            Каждое сохранение создаёт новую версию в <code>article_versions</code>. История
            доступна через БД (UI в дальнейшем).
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Контент" subtitle={list ? `${list.length} показано` : undefined} />
      <div className="p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tg-hint" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по статьям…"
            className="w-full h-12 pl-10 pr-3 bg-tg-secondaryBg rounded-xl outline-none"
          />
        </div>

        {isLoading && <div className="text-tg-hint">Загрузка…</div>}

        {list?.map((a) => (
          <Card
            key={a.id}
            className="cursor-pointer hover:bg-tg-secondaryBg/70"
            onClick={() => setOpenId(a.id)}
          >
            <div className="font-semibold">{a.title}</div>
            {a.summaryMd && (
              <div className="text-sm text-tg-hint mt-1 line-clamp-2">{a.summaryMd}</div>
            )}
            {a.tags.length > 0 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {a.tags.slice(0, 4).map((t) => (
                  <span
                    key={t}
                    className="text-[10px] px-2 py-0.5 bg-tg-secondaryBg rounded-full text-tg-hint"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
